import React, { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { DashboardMock } from "../mocks/DashboardMocks";
import { scrollToId } from "../../hooks/useLenis";
import { logAnalytics } from "../../lib/api";
import { EVENT_CONFIG } from "../../config/event";

/**
 * Kinetic hero – signature on-load moment:
 * • badge slides down
 * • title reveals line-by-line via clip-path masks
 * • subtitle fades word-by-word
 * • dashboard mockup floats in with parallax to cursor
 */
const words = "SafetyDesk81 riunisce in un'unica piattaforma scadenze, formazione, visite mediche, sopralluoghi, documenti e adempimenti aziendali.".split(" ");

export default function Hero() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const smx = useSpring(mx, { stiffness: 80, damping: 20 });
    const smy = useSpring(my, { stiffness: 80, damping: 20 });
    const rx = useTransform(smy, [-40, 40], [4, -4]);
    const ry = useTransform(smx, [-40, 40], [-4, 4]);
    const tx = useTransform(smx, [-40, 40], [-10, 10]);
    const ty = useTransform(smy, [-40, 40], [-6, 6]);

    useEffect(() => {
        const on = (e) => {
            const w = window.innerWidth, h = window.innerHeight;
            mx.set(((e.clientX - w / 2) / w) * 80);
            my.set(((e.clientY - h / 2) / h) * 80);
        };
        window.addEventListener("mousemove", on);
        return () => window.removeEventListener("mousemove", on);
    }, [mx, my]);

    return (
        <section id="hero" className="relative pt-[110px] md:pt-[130px] pb-16 md:pb-24 overflow-hidden bg-[radial-gradient(1200px_600px_at_80%_-10%,#EDE3FD_0%,transparent_60%),radial-gradient(900px_500px_at_-10%_10%,#E4ECFD_0%,transparent_55%),#F4F6FB]">
            {/* animated blobs */}
            <motion.div
                aria-hidden
                className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-gold-100 blur-3xl opacity-70 animate-blob"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                transition={{ duration: 1.4, delay: 0.4 }}
            />
            <div aria-hidden className="absolute inset-0 bg-grid-light opacity-40 pointer-events-none" />

            <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">
                <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-10 lg:gap-14 items-center">
                    {/* Left column */}
                    <div>
                        {/* Prominent event date banner */}
                        <motion.div
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                            className="max-w-[560px] rounded-2xl bg-navy-950 text-white overflow-hidden shadow-card"
                            data-testid="hero-event-banner"
                        >
                            <div className="flex items-stretch">
                                <div className="bg-gold-400 text-navy-950 px-4 py-3 flex flex-col items-center justify-center min-w-[86px]">
                                    <div className="text-[9px] font-mono font-bold uppercase tracking-widest opacity-70">Ott</div>
                                    <div className="font-display font-black text-3xl md:text-4xl leading-none">01</div>
                                    <div className="text-[9px] font-mono font-bold uppercase tracking-widest opacity-70">'26</div>
                                </div>
                                <div className="flex-1 px-4 py-3 flex flex-col justify-center">
                                    <div className="text-[10px] font-mono font-bold uppercase tracking-widest text-gold-400">Evento ufficiale</div>
                                    <div className="font-display font-black text-lg md:text-xl leading-tight mt-0.5">Giovedì 1 ottobre 2026</div>
                                    <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-white/85">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" />
                                        POSTI LIMITATI — Prenota subito
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Masked reveal title (single block, natural wrap) */}
                        <h1
                            className="mt-6 font-display font-extrabold tracking-tight text-navy-950 leading-[1.05] text-[34px] sm:text-[44px] lg:text-[54px] max-w-[720px]"
                            data-testid="hero-title"
                        >
                            <span className="block overflow-hidden pb-2">
                                <motion.span
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 0.95, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    className="block"
                                >
                                    La sicurezza aziendale finalmente sotto controllo grazie alla{" "}
                                    <span className="relative inline-block">
                                        <span className="relative z-10">consulenza agentica</span>
                                        <motion.span
                                            aria-hidden
                                            initial={{ scaleX: 0 }}
                                            animate={{ scaleX: 1 }}
                                            transition={{ duration: 0.7, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
                                            className="absolute left-0 right-0 bottom-0.5 h-[6px] md:h-[8px] bg-gold-300/80 z-0 origin-left"
                                        />
                                    </span>
                                    .
                                </motion.span>
                            </span>
                        </h1>

                        {/* Subtitle: word by word */}
                        <p className="mt-6 max-w-[560px] text-navy-700 text-[17px] md:text-lg leading-relaxed" data-testid="hero-sub">
                            {words.map((w, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
                                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                    transition={{ duration: 0.35, delay: 1.05 + i * 0.02, ease: "easeOut" }}
                                    className="inline-block mr-[0.25em]"
                                >
                                    {w}
                                </motion.span>
                            ))}
                        </p>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 1.75 }}
                            className="mt-4 inline-block bg-gold-200/70 text-navy-950 text-[15px] leading-relaxed px-4 py-2.5 rounded-lg max-w-[600px] border-l-4 border-gold-400"
                        >
                            Addio ai registri cartacei: pulizie, temperature e addestramenti si compilano e si gestiscono online, con documenti sempre ordinati e scaricabili in un solo clic.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1.85, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-8 flex flex-wrap gap-3"
                        >
                            <button
                                data-testid="hero-cta-register"
                                onClick={() => { logAnalytics("cta_click", { source: "hero" }); scrollToId("iscriviti"); }}
                                className="group inline-flex items-center gap-2 bg-navy-950 hover:bg-navy-900 text-white font-semibold text-[15px] px-6 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-card"
                            >
                                Prenota il tuo posto
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                            </button>
                            <button
                                data-testid="hero-cta-discover"
                                onClick={() => { logAnalytics("secondary_cta_click", { source: "hero" }); scrollToId("piattaforma"); }}
                                className="inline-flex items-center gap-2 bg-white hover:bg-navy-50 text-navy-950 font-semibold text-[15px] px-6 py-3.5 rounded-full border border-navy-100 transition-all duration-300 hover:-translate-y-0.5"
                            >
                                Scopri SafetyDesk81
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 2.05 }}
                            className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-navy-600 max-w-lg"
                        >
                            <span className="inline-flex items-center gap-1.5 font-semibold text-navy-950">
                                <span className="w-1.5 h-1.5 rounded-full bg-gold-400" /> Chatbot AI integrato
                            </span>
                            <span className="inline-flex items-center gap-1.5 font-semibold text-navy-950">
                                <span className="w-1.5 h-1.5 rounded-full bg-gold-400" /> Registri completamente digitali
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-navy-500">
                                <span className="w-1.5 h-1.5 rounded-full bg-navy-300" /> Posti limitati
                            </span>
                        </motion.div>
                    </div>

                    {/* Right column: dashboard mockup with parallax */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        style={{ perspective: 1200 }}
                        className="relative"
                    >
                        <motion.div
                            style={{ rotateX: rx, rotateY: ry, x: tx, y: ty }}
                            className="relative"
                        >
                            {/* floating pill labels */}
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, delay: 1.5 }}
                                className="hidden lg:flex absolute -left-8 top-12 z-10 items-center gap-2 bg-white rounded-full pl-2 pr-4 py-2 shadow-card border border-navy-100"
                            >
                                <span className="w-6 h-6 rounded-full bg-mint text-emerald-700 flex items-center justify-center text-[10px] font-bold">✓</span>
                                <div className="text-[11px]">
                                    <div className="font-semibold text-navy-900">2311 corsi validi</div>
                                    <div className="text-navy-500 text-[10px]">aggiornato oggi</div>
                                </div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, delay: 1.7 }}
                                className="hidden lg:flex absolute -right-6 bottom-16 z-10 items-center gap-2 bg-navy-950 text-white rounded-full pl-2 pr-4 py-2 shadow-card"
                            >
                                <span className="w-6 h-6 rounded-full bg-gold-400 text-navy-950 flex items-center justify-center text-[10px] font-bold">!</span>
                                <div className="text-[11px]">
                                    <div className="font-semibold">959 scadenze critiche</div>
                                    <div className="text-white/60 text-[10px]">visibili in un colpo d'occhio</div>
                                </div>
                            </motion.div>
                            <DashboardMock animate={false} />
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* subtle scroll indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.4, duration: 0.5 }}
                className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 items-center gap-2 text-navy-400 text-[11px] font-mono uppercase tracking-widest"
            >
                <span>Scroll</span>
                <motion.span
                    animate={{ y: [0, 6, 0] }}
                    transition={{ repeat: Infinity, duration: 1.6 }}
                    className="inline-block"
                >
                    ↓
                </motion.span>
            </motion.div>
        </section>
    );
}

function MaskedLine({ children, delay = 0 }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
                className="block"
            >
                {children}
            </motion.span>
        </span>
    );
}
